import { forwardRef, CSSProperties } from "react";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { MDTypographyProps } from "./types";

const MDTypography = forwardRef<HTMLSpanElement, MDTypographyProps>(
  (
    {
      color = "dark",
      fontWeight = false,
      textTransform = "none",
      verticalAlign = "unset",
      textGradient = false,
      opacity = 1,
      children,
      style,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();
    const { palette, typography } = theme;
    
    // For now, we'll use a simple darkMode detection or default to false
    const darkMode = false;

    // fontWeight styles
    const fontWeights = {
      light: typography.fontWeightLight,
      regular: typography.fontWeightRegular,
      medium: typography.fontWeightMedium,
      bold: typography.fontWeightBold,
    };

    // color value
    let colorValue: string;
    
    if (color === "inherit") {
      colorValue = "inherit";
    } else if (color === "text") {
      colorValue = palette.text.primary;
    } else if (color === "white") {
      colorValue = palette.common.white;
    } else if (color === "dark") {
      colorValue = darkMode ? palette.common.white : palette.text.primary;
    } else {
      const paletteColor = palette[color as keyof typeof palette] as any;
      if (paletteColor && typeof paletteColor === 'object' && paletteColor.main) {
        colorValue = paletteColor.main;
      } else {
        colorValue = palette.text.primary;
      }
    }

    // styles for the typography with textGradient={true}
    const gradientStyles: CSSProperties = textGradient ? {
      background: `linear-gradient(195deg, ${palette.primary.main}, ${palette.primary.dark})`,
      backgroundClip: "text",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      display: "inline-block",
      position: "relative",
      zIndex: 1,
    } : {};

    const fontWeightValue = fontWeight ? (fontWeights[fontWeight] || fontWeight) : undefined;

    const combinedStyle: CSSProperties = {
      opacity,
      textTransform,
      verticalAlign,
      textDecoration: "none",
      color: colorValue,
      ...(fontWeightValue && { fontWeight: fontWeightValue }),
      ...gradientStyles,
      ...style,
    };

    return (
      <Typography
        {...rest}
        ref={ref}
        style={combinedStyle}
      >
        {children}
      </Typography>
    );
  }
);

MDTypography.displayName = "MDTypography";

export default MDTypography;